import { translate } from '@/utilities';
import capitalizeFirstLetter from '@/utilities/capitalizeFirstLetter';
import constants from '@/utilities/constants';
import {
  differenceInMilliseconds,
  isToday,
  isTomorrow,
  isYesterday,
  isValid,
  toDate,
} from 'date-fns';

const getIntervalText = (dueDate) => {
  if (isToday(dueDate)) return translate('today');
  if (isTomorrow(dueDate)) return translate('tomorrow');
  if (isYesterday(dueDate)) return translate('yesterday');

  return translate('today');
};

// Add char ~ to the start of the string if unit is year or month and capitalize the first letter
const generateReadableLabel = (string, unit) => {
  const prefix = unit === 'year' || unit === 'month' ? '~' : '';
  return `${prefix} ${capitalizeFirstLetter(string)}`;
};

/**
 * Human readable elapsed or remaining time (example: "3 minutes ago", "~ 2 months")
 * @param {Date|number|string} date - A Date object, timestamp or string parsable with Date.parse()
 * @param {Date|number|string} [nowDate=Date.now()] - A Date object, timestamp or string parsable with Date.parse()
 * @param {Intl.RelativeTimeFormat} [rft] - A Intl formatter for relative time formatting
 * @returns {string} Human readable elapsed or remaining time, or empty string if dates are invalid
 */
const getHumanReadableDate = (
  date,
  nowDate = Date.now(),
  rft = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
) => {
  const { DAY, MONTH, YEAR } = constants;
  const dueDate = toDate(date);
  const now = toDate(nowDate);
  if (!isValid(dueDate) || !isValid(now)) {
    return '';
  }

  const diff = differenceInMilliseconds(now, dueDate);
  const diffAbs = Math.abs(diff);
  const intervalText = getIntervalText(dueDate);
  const intervals = [
    { threshold: YEAR, divisor: YEAR, unit: 'year' },
    { threshold: MONTH, divisor: MONTH, unit: 'month' },
    { threshold: DAY, divisor: DAY, unit: 'day' },
    { threshold: 0, divisor: 1, text: intervalText },
  ];

  for (const interval of intervals) {
    if (diffAbs >= interval.threshold) {
      const value = Math.round(diffAbs / interval.divisor);
      const isFuture = diff < 0;
      const formatValue = isFuture ? value : -value;

      return interval.unit
        ? generateReadableLabel(rft.format(formatValue, interval.unit), interval.unit)
        : generateReadableLabel(interval.text);
    }
  }
  return '';
};

export { getHumanReadableDate, getIntervalText, generateReadableLabel };
